import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Client } from "openapi-fetch";
import type { paths, components } from "../api/generated";
import { createPaginatedInfiniteQuery, type QueryApi, type SpringPagedResponse } from "../api/query";

type FavouriteShopCountResponse = components["schemas"]["FavouriteShopCountResponse"];
type FavouriteShopCheckResponse = components["schemas"]["FavouriteShopCheckResponse"];
type FavouriteShopBatchCheckResponse = components["schemas"]["FavouriteShopBatchCheckResponse"];
export type ShopPlainDto = components["schemas"]["ShopPlainDto"];
type FavouriteShopsPage = SpringPagedResponse<ShopPlainDto>;
type FetchClient = Client<paths, "*/*">;

const STALE_TIME_MS = 30_000;

export const favouriteShopKeys = {
  count: () => ["get", "/favourite-shops/count", {}] as const,
  check: (shopId: string) =>
    ["get", "/favourite-shops/check/{shopId}", { params: { path: { shopId } } }] as const,
  list: () => ["get", "/favourite-shops"] as const,
  batchCheck: () => ["post", "/favourite-shops/check-batch"] as const,
};

export function createUseFavouriteShopCount($api: QueryApi) {
  return function useFavouriteShopCount(options?: { enabled?: boolean }) {
    return $api.useQuery("get", "/favourite-shops/count", {}, {
      staleTime: STALE_TIME_MS,
      select: (data: FavouriteShopCountResponse) => data?.count ?? 0,
      ...options,
    });
  };
}

export function createUseIsFavourited($api: QueryApi) {
  return function useIsFavourited(shopId: string | undefined) {
    return $api.useQuery(
      "get",
      "/favourite-shops/check/{shopId}",
      { params: { path: { shopId: shopId! } } },
      {
        enabled: !!shopId,
        staleTime: STALE_TIME_MS,
        select: (data: FavouriteShopCheckResponse) => data?.favourited ?? false,
      },
    );
  };
}

export function createUseFavouriteShopsInfinite($api: QueryApi) {
  const usePaginatedInfiniteQuery = createPaginatedInfiniteQuery($api);

  return function useFavouriteShopsInfinite(size = 20, options?: { enabled?: boolean }) {
    return usePaginatedInfiniteQuery<FavouriteShopsPage, ShopPlainDto>(
      "/favourite-shops",
      { size },
      options,
    );
  };
}

export function createUseFavouriteShopBatchCheck($api: QueryApi) {
  return function useFavouriteShopBatchCheck(shopIds: string[]) {
    return $api.useQuery(
      "post",
      "/favourite-shops/check-batch",
      { body: { shopIds } },
      {
        enabled: shopIds.length > 0,
        staleTime: STALE_TIME_MS,
        select: (data: FavouriteShopBatchCheckResponse) => data?.results ?? {},
      },
    );
  };
}

export function createUseToggleFavouriteShop(
  $fetch: FetchClient,
  options?: { onSettledExtra?: (queryClient: QueryClient) => void },
) {
  return function useToggleFavouriteShop() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ shopId, favourited }: { shopId: string; favourited: boolean }) => {
        if (favourited) {
          await $fetch.DELETE("/favourite-shops/{shopId}", {
            params: { path: { shopId } },
          });
        } else {
          await $fetch.POST("/favourite-shops/{shopId}", {
            params: { path: { shopId } },
          });
        }
      },
      onMutate: async ({ shopId, favourited }) => {
        const checkKey = favouriteShopKeys.check(shopId);
        const countKey = favouriteShopKeys.count();

        await queryClient.cancelQueries({ queryKey: checkKey });
        await queryClient.cancelQueries({ queryKey: countKey });

        const previousCheck = queryClient.getQueryData(checkKey);
        const previousCount = queryClient.getQueryData(countKey);

        queryClient.setQueryData(checkKey, { favourited: !favourited });
        queryClient.setQueryData(countKey, (old: FavouriteShopCountResponse | undefined) => {
          const current = old?.count ?? 0;
          return { count: favourited ? Math.max(0, current - 1) : current + 1 };
        });

        return { previousCheck, previousCount, checkKey, countKey };
      },
      onError: (_err, _vars, context) => {
        if (context) {
          queryClient.setQueryData(context.checkKey, context.previousCheck);
          queryClient.setQueryData(context.countKey, context.previousCount);
        }
      },
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries({ queryKey: favouriteShopKeys.list() });
        queryClient.invalidateQueries({ queryKey: favouriteShopKeys.check(variables.shopId) });
        queryClient.invalidateQueries({ queryKey: favouriteShopKeys.count() });
        queryClient.invalidateQueries({ queryKey: favouriteShopKeys.batchCheck() });
        options?.onSettledExtra?.(queryClient);
      },
    });
  };
}
